import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from './Button.jsx';
import { useCreateAssociate, useUpdateAssociate } from '../hooks/useAssociates.js';

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

function Field({ label, children, error, required }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function emptyForm() {
  return {
    kind: 'person',
    first_name: '',
    last_name: '',
    corporate_name: '',
    siren: '',
    email: '',
    notes: '',
    status: 'active',
  };
}

function formFromAssociate(associate) {
  if (!associate) return emptyForm();
  return {
    kind:           associate.kind ?? 'person',
    first_name:     associate.first_name ?? '',
    last_name:      associate.last_name ?? '',
    corporate_name: associate.corporate_name ?? '',
    siren:          associate.siren ?? '',
    email:          associate.email ?? '',
    notes:          associate.notes ?? '',
    status:         associate.status ?? 'active',
  };
}

function validate(form) {
  const errors = {};
  if (form.kind === 'person') {
    if (!form.first_name.trim()) errors.first_name = 'Prénom requis';
    if (!form.last_name.trim())  errors.last_name  = 'Nom requis';
  } else {
    if (!form.corporate_name.trim()) errors.corporate_name = 'Raison sociale requise';
  }
  if (form.siren && !/^\d{9}$/.test(form.siren.trim())) {
    errors.siren = 'Le SIREN doit contenir exactement 9 chiffres';
  }
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Email invalide';
  }
  return errors;
}

/**
 * AssociateForm — slide-over modal pour créer ou éditer un associé.
 *
 * Props :
 *   open      : boolean
 *   onClose   : () => void
 *   associate : object | undefined — si fourni, mode édition
 */
export default function AssociateForm({ open, onClose, associate }) {
  const isEdit = Boolean(associate);
  const createAssociate = useCreateAssociate();
  const updateAssociate = useUpdateAssociate();
  const mutation = isEdit ? updateAssociate : createAssociate;

  const [form, setForm]   = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(formFromAssociate(associate));
      setErrors({});
      mutation.reset?.();
    }
  }, [open, associate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
      siren: form.siren.trim() || null,
    };
    if (form.kind === 'person') {
      payload.first_name = form.first_name.trim();
      payload.last_name  = form.last_name.trim();
      payload.corporate_name = null;
    } else {
      payload.corporate_name = form.corporate_name.trim();
      payload.first_name = null;
      payload.last_name  = null;
    }
    if (!isEdit) payload.kind = form.kind;
    if (isEdit) payload.status = form.status;

    try {
      if (isEdit) {
        await mutation.mutateAsync({ id: associate.id, ...payload });
      } else {
        await mutation.mutateAsync(payload);
      }
      onClose?.();
    } catch (err) {
      if (err.code === 'CONFLICT' || err.status === 409) {
        setErrors({ email: 'Cet email est déjà utilisé par un autre associé' });
      }
    }
  }

  const isDisabled = mutation.isPending;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-200 bg-black/30
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Modifier l\'associé' : 'Nouvel associé'}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out
          inset-x-0 bottom-0 rounded-t-2xl max-h-[95dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[480px] lg:rounded-none lg:max-h-none
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Modifier l\'associé' : 'Nouvel associé'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-5 space-y-4">
          <Field label="Type d'associé" required>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'person',  label: 'Personne physique' },
                { value: 'company', label: 'Personne morale'   },
              ].map((opt) => {
                const selected = form.kind === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => !isEdit && set('kind', opt.value)}
                    disabled={isDisabled || isEdit}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition
                      ${selected
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}
                      ${isEdit ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {isEdit && (
              <p className="text-xs text-slate-500 mt-1">
                Le type est figé à la création.
              </p>
            )}
          </Field>

          {form.kind === 'person' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prénom" error={errors.first_name} required>
                <input
                  type="text"
                  className={inputClass}
                  value={form.first_name}
                  onChange={(e) => set('first_name', e.target.value)}
                  placeholder="Victor"
                  disabled={isDisabled}
                />
              </Field>

              <Field label="Nom" error={errors.last_name} required>
                <input
                  type="text"
                  className={inputClass}
                  value={form.last_name}
                  onChange={(e) => set('last_name', e.target.value)}
                  placeholder="Griffon"
                  disabled={isDisabled}
                />
              </Field>
            </div>
          ) : (
            <>
              <Field label="Raison sociale" error={errors.corporate_name} required>
                <input
                  type="text"
                  className={inputClass}
                  value={form.corporate_name}
                  onChange={(e) => set('corporate_name', e.target.value)}
                  placeholder="Holding XYZ SAS"
                  disabled={isDisabled}
                />
              </Field>

              <Field label="SIREN" error={errors.siren}>
                <input
                  type="text"
                  inputMode="numeric"
                  className={inputClass}
                  value={form.siren}
                  onChange={(e) => set('siren', e.target.value.replace(/\D/g, '').slice(0, 9))}
                  placeholder="123456789"
                  disabled={isDisabled}
                />
              </Field>
            </>
          )}

          <Field label="Email" error={errors.email}>
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="victor@example.com"
              disabled={isDisabled}
            />
          </Field>

          {isEdit && (
            <Field label="Statut">
              <select
                className={inputClass}
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                disabled={isDisabled}
              >
                <option value="active">Actif</option>
                <option value="archived">Archivé</option>
              </select>
            </Field>
          )}

          <Field label="Notes">
            <textarea
              className={`${inputClass} resize-none`}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Informations complémentaires…"
              disabled={isDisabled}
            />
          </Field>

          {mutation.error && !errors.email && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {mutation.error.message}
            </p>
          )}

          <div className="flex gap-2 pt-2 pb-safe">
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={isDisabled}
              className="flex-1"
            >
              {isEdit ? 'Enregistrer' : 'Créer l\'associé'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
